import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

const NotificationSettings = ({ userId, userType, settings, onSave }) => {
  const [localSettings, setLocalSettings] = useState(settings || {
    email_notifications: true,
    event_reminders: true,
    event_updates: true,
    new_events: false,
    speaker_alerts: true,
    marketing_emails: false
  });
  const [saving, setSaving] = useState(false);

  const handleToggle = (key) => {
    setLocalSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          notification_settings: localSettings
        })
        .eq('id', userId);

      if (error) throw error;
      
      if (onSave) {
        onSave(localSettings);
      }
      
      alert('Notification settings saved successfully!');
    } catch (error) {
      console.error('Error saving notification settings:', error);
      alert('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Email Notifications</h3>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Email Notifications</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Receive email notifications about your account</p>
          </div>
          <button
            onClick={() => handleToggle('email_notifications')}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              localSettings.email_notifications ? 'bg-primary' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                localSettings.email_notifications ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Event Reminders</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {userType === 'attendee' 
                ? 'Get reminded about events you registered for' 
                : 'Remind attendees about your upcoming events'
              }
            </p>
          </div>
          <button
            onClick={() => handleToggle('event_reminders')}
            disabled={!localSettings.email_notifications}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              localSettings.event_reminders && localSettings.email_notifications ? 'bg-primary' : 'bg-gray-200'
            } ${!localSettings.email_notifications ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                localSettings.event_reminders && localSettings.email_notifications ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Event Updates</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {userType === 'attendee' 
                ? 'Receive updates for events you registered for' 
                : 'Notify about changes to your events'
              }
            </p>
          </div>
          <button
            onClick={() => handleToggle('event_updates')}
            disabled={!localSettings.email_notifications}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              localSettings.event_updates && localSettings.email_notifications ? 'bg-primary' : 'bg-gray-200'
            } ${!localSettings.email_notifications ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                localSettings.event_updates && localSettings.email_notifications ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {userType === 'attendee' && (
          <>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">New Events</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Get notified about new events in your area</p>
            </div>
            <button
              onClick={() => handleToggle('new_events')}
              disabled={!localSettings.email_notifications}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localSettings.new_events && localSettings.email_notifications ? 'bg-primary' : 'bg-gray-200'
              } ${!localSettings.email_notifications ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localSettings.new_events && localSettings.email_notifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Speaker Alerts</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Get notified when your favorite speakers are added to events</p>
            </div>
            <button
              onClick={() => handleToggle('speaker_alerts')}
              disabled={!localSettings.email_notifications}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                localSettings.speaker_alerts && localSettings.email_notifications ? 'bg-primary' : 'bg-gray-200'
              } ${!localSettings.email_notifications ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  localSettings.speaker_alerts && localSettings.email_notifications ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          </>
        )}

        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Marketing Emails</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Receive promotional emails and newsletters</p>
          </div>
          <button
            onClick={() => handleToggle('marketing_emails')}
            disabled={!localSettings.email_notifications}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              localSettings.marketing_emails && localSettings.email_notifications ? 'bg-primary' : 'bg-gray-200'
            } ${!localSettings.email_notifications ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                localSettings.marketing_emails && localSettings.email_notifications ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;
